// Billing page (ADR-179 P2/P3 / D5-P2/D2.2). Reads GetMyContractStatus —
// the account-scoped Console CONTRACT/license surface — PLUS (P3)
// ListMyOrgBillingPortals for the per-org Stripe self-service link, and the
// two notify-operator forms (RequestSeatChange/RequestCancellation) in
// place of any inline mutation, per section 7-D's confirmed "no
// self-cancel, no self-seat-change" stance.
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto the shared apps-future/portal/src/lib/genesis.ts tokens + glass-card
// idiom, matching the operator console and the ui/ app shell. Data layer
// (queries, mutations) untouched.
import { useState } from 'react'
import { friendlyError } from '@/lib/auth/auth-http'
import { useQuery } from '@tanstack/react-query'
import { getMyContractStatus, listMyOrgBillingPortals, requestSeatChange, requestCancellation } from '@/lib/auth/customer-api'
import { formatTimestamp } from '@/lib/formatters'
import { DARK, GOLD, GREY_300, GREY_400, GREY_500, SUCCESS, WHITE, errorBlockStyle, primaryButtonCompactStyle } from '@/lib/genesis'

export function PortalBillingPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portal-contract-status'],
    queryFn: () => getMyContractStatus(),
    retry: false,
  })
  // "No license yet" is a NORMAL state for a fresh account, not a failure --
  // the server signals it with a specific NotFound message (see
  // customer_rpcs.go GetMyContractStatus).
  const noLicenseYet =
    isError && error instanceof Error && error.message.includes('no license on this account')
  const {
    data: portalsData,
    isLoading: portalsLoading,
    isError: portalsError,
  } = useQuery({
    queryKey: ['portal-org-billing-portals'],
    queryFn: () => listMyOrgBillingPortals(),
  })

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Billing</h1>

        {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading contract status…</p>}
        {noLicenseYet && (
          <p className="text-sm" style={{ color: GREY_400 }}>
            No contract on this account yet. Your contract details will appear here once a license
            is issued to your account.
          </p>
        )}
        {isError && !noLicenseYet && (
          <div className="p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>
              {friendlyError(error, 'Failed to load contract status.')}
            </p>
          </div>
        )}

        {!isLoading && !isError && data && (
          <div className="glass-card max-w-xl p-5">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
              <dt style={{ color: GREY_400 }}>Subscription status</dt>
              <dd style={{ color: WHITE }}>
                {data.getStripeSubscriptionStatus() || 'Not set'}
              </dd>

              <dt style={{ color: GREY_400 }}>Current period ends</dt>
              <dd className="font-mono" style={{ color: WHITE }}>{formatTimestamp(data.getCurrentPeriodEnd())}</dd>

              <dt style={{ color: GREY_400 }}>Billing reference</dt>
              <dd className="font-mono text-xs" style={{ color: GREY_400 }}>
                {data.getStripeCustomerId() || '—'}
              </dd>

              <dt style={{ color: GREY_400 }}>Data source</dt>
              <dd style={{ color: WHITE }}>
                {data.getFromLiveStripeRead() ? 'Live' : 'Cached'}
              </dd>
            </dl>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold" style={{ color: WHITE }}>Organization billing portals</h2>
        {portalsLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading…</p>}
        {portalsError && (
          <div className="p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>Failed to load organization billing links.</p>
          </div>
        )}
        {!portalsLoading && !portalsError && portalsData && (
          <>
            {portalsData.getBillingUnavailable() && (
              <div className="mb-3 px-3 py-2" style={{ backgroundColor: DARK, borderLeft: '3px solid #FBBF24', borderRadius: '8px' }}>
                <p className="text-sm" style={{ color: WHITE }}>
                  Billing self-service is temporarily unavailable. Your organizations are still listed
                  below — contact your account team for billing changes in the meantime.
                </p>
              </div>
            )}
            {portalsData.getPortalsList().length === 0 && (
              <p className="text-sm" style={{ color: GREY_400 }}>No organizations on this account yet.</p>
            )}
            <div className="flex flex-col gap-2">
              {portalsData.getPortalsList().map((p) => (
                <div key={p.getOrgId()} className="glass-card flex items-center justify-between px-4 py-3">
                  <span className="text-sm" style={{ color: GREY_300 }}>{p.getOrgName()}</span>
                  {p.getPortalUrl() ? (
                    <a
                      href={p.getPortalUrl()}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg px-2.5 py-1 text-xs"
                      style={{ border: `1px solid ${GOLD}`, color: GOLD }}
                    >
                      Open billing portal
                    </a>
                  ) : (
                    <span className="text-xs" style={{ color: GREY_500 }}>Not available</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <NotifyOperatorForms />

      <p className="max-w-md text-xs" style={{ color: GREY_500 }}>
        Itemized invoices and payment methods are managed through Stripe's portal above, where
        available. For anything else, contact your account team.
      </p>
    </div>
  )
}

// NotifyOperatorForms implements the ADR-179 section 7-D notify-operator
// path: RequestSeatChange/RequestCancellation write a durable, operator-
// visible ticket and return immediately — there is no self-cancel or
// self-seat-change RPC to call instead.
function NotifyOperatorForms() {
  const [seats, setSeats] = useState('')
  const [seatDetail, setSeatDetail] = useState('')
  const [seatSubmitting, setSeatSubmitting] = useState(false)
  const [seatSent, setSeatSent] = useState(false)
  const [seatError, setSeatError] = useState<string | null>(null)

  const [cancelDetail, setCancelDetail] = useState('')
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [cancelSent, setCancelSent] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  async function handleSeatSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSeatError(null)
    setSeatSubmitting(true)
    try {
      await requestSeatChange(Number(seats) || 0, seatDetail)
      setSeatSent(true)
    } catch (err) {
      setSeatError(err instanceof Error ? err.message : 'Failed to send request.')
    } finally {
      setSeatSubmitting(false)
    }
  }

  async function handleCancelSubmit(e: React.FormEvent) {
    e.preventDefault()
    setCancelError(null)
    setCancelSubmitting(true)
    try {
      await requestCancellation(cancelDetail)
      setCancelSent(true)
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to send request.')
    } finally {
      setCancelSubmitting(false)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="glass-card p-4">
        <h2 className="mb-1 text-sm font-semibold" style={{ color: WHITE }}>Request a seat change</h2>
        <p className="mb-3 text-xs" style={{ color: GREY_500 }}>
          Seat changes are handled by your account team — this sends them a request, it does not
          change your plan directly.
        </p>
        {seatSent ? (
          <div className="px-3 py-2" style={{ backgroundColor: DARK, borderLeft: `3px solid ${SUCCESS}`, borderRadius: '8px' }}>
            <p className="text-sm" style={{ color: GREY_300 }}>
              Your request has been sent to your account team.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSeatSubmit} className="flex flex-col gap-2">
            <input
              type="number"
              min={0}
              placeholder="Requested seat count (optional)"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              className="glass-input px-3 py-2 text-sm"
              style={{ color: WHITE }}
            />
            <textarea
              placeholder="Anything else your account team should know?"
              value={seatDetail}
              onChange={(e) => setSeatDetail(e.target.value)}
              rows={2}
              className="glass-input px-3 py-2 text-sm"
              style={{ color: WHITE }}
            />
            {seatError && (
              <div className="p-2" style={errorBlockStyle}>
                <p className="text-xs" style={{ color: WHITE }}>{seatError}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={seatSubmitting}
              className="self-start rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              style={primaryButtonCompactStyle}
            >
              {seatSubmitting ? 'Sending…' : 'Send request'}
            </button>
          </form>
        )}
      </div>

      <div className="glass-card p-4">
        <h2 className="mb-1 text-sm font-semibold" style={{ color: WHITE }}>Request cancellation</h2>
        <p className="mb-3 text-xs" style={{ color: GREY_500 }}>
          Cancellation is handled by your account team — this sends them a request, it does not
          cancel your contract directly.
        </p>
        {cancelSent ? (
          <div className="px-3 py-2" style={{ backgroundColor: DARK, borderLeft: `3px solid ${SUCCESS}`, borderRadius: '8px' }}>
            <p className="text-sm" style={{ color: GREY_300 }}>
              Your request has been sent to your account team.
            </p>
          </div>
        ) : (
          <form onSubmit={handleCancelSubmit} className="flex flex-col gap-2">
            <textarea
              placeholder="Let your account team know why (optional)"
              value={cancelDetail}
              onChange={(e) => setCancelDetail(e.target.value)}
              rows={3}
              className="glass-input px-3 py-2 text-sm"
              style={{ color: WHITE }}
            />
            {cancelError && (
              <div className="p-2" style={errorBlockStyle}>
                <p className="text-xs" style={{ color: WHITE }}>{cancelError}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={cancelSubmitting}
              className="self-start rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              style={{ border: '1px solid #F27B76', color: '#F27B76', backgroundColor: 'transparent' }}
            >
              {cancelSubmitting ? 'Sending…' : 'Send request'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
